import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ArrowDown, ArrowUp, SearchX } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, PillTabs, RelativeTime, SearchField, TablePagination, formatNumber } from '../../../components/design-system/admin';
import type { Assumptions, EconomicsRow } from '../../../common/models/unit-economics.model';
import { money, moneyShort } from '../costs/cost-format';
import { FlagBadges } from './FlagBadges';
import { RowFilter, SortKey, filterRows, formatMultiple, sortRows } from './ue-format';

/** "Growth · Card · billed monthly" — the plan and where its price was read from. */
export function billingLine(r: EconomicsRow, t: TFunction): string {
  return [
    r.plan.name,
    String(t(`UNIT_ECONOMICS.STATUS.${r.billing.status}`)),
    r.billing.status === 'paying' ? String(t(`UNIT_ECONOMICS.SOURCE.${r.billing.source}`)) : null,
    r.billing.status === 'paying' && r.billing.interval ? String(t(`UNIT_ECONOMICS.INTERVAL.${r.billing.interval}`)) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Paying above, at or below the list price. */
export function listLine(r: EconomicsRow, t: TFunction): string | null {
  const d = r.list.discountPct;
  if (d == null) return null;
  if (Math.abs(d) < 0.5) return String(t('UNIT_ECONOMICS.TABLE.AT_LIST'));
  return String(d > 0 ? t('UNIT_ECONOMICS.TABLE.BELOW_LIST', { pct: formatNumber(d) }) : t('UNIT_ECONOMICS.TABLE.ABOVE_LIST', { pct: formatNumber(-d) }));
}

/**
 * Every customer for the month: MRR, cost to serve, margin, the sales we
 * influenced and the return on what they pay, flags, last activity. Sortable
 * by any number; a row opens the customer's detail.
 */
export function CustomersTable({
  rows,
  assumptions,
  loading,
  onOpen,
}: {
  rows: EconomicsRow[] | null;
  assumptions: Assumptions | null;
  loading?: boolean;
  onOpen: (row: EconomicsRow) => void;
}) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'mrr', desc: true });
  const [filter, setFilter] = useState<RowFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const shownAll = useMemo(() => (rows ? sortRows(filterRows(rows, filter, search), sort.key, sort.desc) : []), [rows, filter, search, sort]);
  const shown = shownAll.slice(page * pageSize, (page + 1) * pageSize);
  const count = (f: RowFilter) => (rows ? filterRows(rows, f, '').length : 0);

  const SortHead = ({ k, children, className }: { k: SortKey; children: ReactNode; className?: string }) => (
    <TableHead className={className} aria-sort={sort.key === k ? (sort.desc ? 'descending' : 'ascending') : undefined}>
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={() => {
          setSort((s) => ({ key: k, desc: s.key === k ? !s.desc : k !== 'name' }));
          setPage(0);
        }}
        data-testid={`ue-sort-${k}`}
      >
        {children}
        {sort.key === k && (sort.desc ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" />)}
      </button>
    </TableHead>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <PillTabs<RowFilter>
          value={filter}
          onChange={(f) => {
            setFilter(f);
            setPage(0);
          }}
          ariaLabel={t('UNIT_ECONOMICS.FILTER.LABEL')}
          options={(['all', 'paying', 'trials', 'flagged'] as RowFilter[]).map((f) => ({ value: f, label: t(`UNIT_ECONOMICS.FILTER.${f.toUpperCase()}`), count: count(f) }))}
        />
        <SearchField
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(0);
          }}
          placeholder={t('UNIT_ECONOMICS.TABLE.SEARCH')}
        />
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        {!rows ? (
          <div className="grid gap-2 p-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : !shownAll.length ? (
          <EmptyState icon={<SearchX />} title={t('UNIT_ECONOMICS.TABLE.EMPTY_TITLE')} description={t('UNIT_ECONOMICS.TABLE.EMPTY_BODY')} />
        ) : (
          <>
            <Table data-testid="ue-table" className={cn(loading && 'opacity-60 transition-opacity')}>
              <TableHeader>
                <TableRow>
                  <SortHead k="name" className="ps-4">{t('UNIT_ECONOMICS.TABLE.CUSTOMER')}</SortHead>
                  <SortHead k="mrr" className="text-end">{t('UNIT_ECONOMICS.TABLE.MRR')}</SortHead>
                  <SortHead k="cost" className="text-end">{t('UNIT_ECONOMICS.TABLE.COST')}</SortHead>
                  <SortHead k="margin" className="text-end">{t('UNIT_ECONOMICS.TABLE.MARGIN')}</SortHead>
                  <SortHead k="influenced" className="text-end">{t('UNIT_ECONOMICS.TABLE.VALUE')}</SortHead>
                  <SortHead k="roi" className="text-end">{t('UNIT_ECONOMICS.TABLE.ROI')}</SortHead>
                  <TableHead>{t('UNIT_ECONOMICS.TABLE.FLAGS')}</TableHead>
                  <SortHead k="lastActive" className="pe-4">{t('UNIT_ECONOMICS.TABLE.LAST_ACTIVE')}</SortHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((r) => {
                  const loss = r.marginUsd != null && r.marginUsd < 0 && r.billing.status === 'paying';
                  return (
                    <TableRow key={r.vendorId} className="cursor-pointer" onClick={() => onOpen(r)} data-testid={`ue-row-${r.vendorId}`}>
                      <TableCell className="max-w-[260px] ps-4">
                        <div className="truncate font-medium">{r.name}</div>
                        <div className="truncate text-xs text-muted-foreground">{billingLine(r, t)}</div>
                      </TableCell>
                      <TableCell className="text-end tabular-nums">
                        {r.billing.mrrUsd == null ? (
                          <span className="text-muted-foreground">{t('UNIT_ECONOMICS.TABLE.NO_REVENUE')}</span>
                        ) : (
                          <>
                            <div>{moneyShort(r.billing.mrrUsd)}</div>
                            <div className="text-xs text-muted-foreground">{listLine(r, t) || ' '}</div>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-end tabular-nums">
                        <div>{money(r.costs.totalUsd)}</div>
                        <div className="text-xs text-muted-foreground">{t('UNIT_ECONOMICS.TABLE.COST_SUB', { direct: money(r.costs.directUsd), shared: money(r.costs.infraShareUsd) })}</div>
                      </TableCell>
                      <TableCell className={cn('text-end tabular-nums', loss && 'text-destructive')}>
                        <div>{r.marginUsd == null ? '—' : money(r.marginUsd)}</div>
                        <div className={cn('text-xs', loss ? 'text-destructive' : 'text-muted-foreground')}>{r.marginPct == null ? ' ' : `${formatNumber(r.marginPct, 1)}%`}</div>
                      </TableCell>
                      <TableCell className="text-end tabular-nums">
                        {r.value.influencedUsd == null ? (
                          <span className="text-xs text-muted-foreground">{t('UNIT_ECONOMICS.TABLE.NO_SHOP')}</span>
                        ) : (
                          <>
                            <div>{moneyShort(r.value.influencedUsd)}</div>
                            <div className="text-xs text-muted-foreground">
                              {r.value.sharePct != null ? t('UNIT_ECONOMICS.TABLE.SHARE', { pct: formatNumber(r.value.sharePct, 1) }) : t('UNIT_ECONOMICS.TABLE.ORDERS', { count: r.value.influencedOrders })}
                            </div>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-end font-medium tabular-nums">{formatMultiple(r.value.roi)}</TableCell>
                      <TableCell className="max-w-[220px]">
                        <FlagBadges flags={r.flags} assumptions={assumptions} />
                      </TableCell>
                      <TableCell className="pe-4 whitespace-nowrap">
                        {r.usage.lastActiveAt ? (
                          <RelativeTime value={r.usage.lastActiveAt} className="text-muted-foreground" />
                        ) : (
                          <span className="text-muted-foreground">{t('UNIT_ECONOMICS.TABLE.NEVER')}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={shownAll.length}
              onPageChange={setPage}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPage(0);
              }}
            />
          </>
        )}
      </Card>
    </div>
  );
}
